import React from 'react';
import { useContainerCtx } from '../../../providers/containerCtx/ContainerCtx';
import { BeneficiaryDataType, BeneficiaryFormValues } from '../types';
import { Text } from '@nbc-design/text';
import { IconButton } from '@nbc-design/button';
import { Edit } from '@nbc-design/icons/lib/web/Edit';
import { Delete } from '@nbc-design/icons/lib/web/Delete';
import BeneficiaryStatus from './BeneficiaryStatus';
import { useBeneficiaryHelpers } from '../hooks/useBeneficiaryHelpers';
import { useBeneficiaryInformationCtx } from '../providers/BeneficiaryInformationCtx';
import { getBeneficiariesNotDeleted, isBeneficiaryProfileComplete } from '../utils';

type BeneficiaryListingItemProps = {
  beneficiary: BeneficiaryDataType;
};

const BeneficiaryListingItem: React.FC<BeneficiaryListingItemProps> = ({ beneficiary }) => {
  const { getText, getAccessibilityText, formValues } = useContainerCtx<BeneficiaryFormValues>();
  const { beneficiaries, account } = formValues;
  const { setBeneficiaryToDelete, setBeneficiaryToEdit } = useBeneficiaryInformationCtx();

  const { isFamilyRespPlanType, areAllContributionFieldsEmpty } = useBeneficiaryHelpers();

  return (
    <div
      key={beneficiary.bncId}
      data-test={`beneficiary-listing-item-${beneficiary.bncId}`}
      className={'bg-white p-4 mb-3 flex justify-between rounded-lg'}
    >
      <div className={'flex'}>
        <div className={'items-center flex mr-4'}>
          <BeneficiaryStatus
            isCompleted={isBeneficiaryProfileComplete(
              beneficiary,
              getBeneficiariesNotDeleted(beneficiaries),
              account?.respPlanTypeCd,
            )}
          />
        </div>

        <div className={'flex flex-col justify-center'}>
          <Text dataTest={'banaficiary-name'}>
            {beneficiary.firstName} {beneficiary.lastName}
          </Text>

          {isFamilyRespPlanType && !areAllContributionFieldsEmpty && (
            <Text>
              {getText('contribution')}
              <strong data-test={'contribution-percent'}>
                {!beneficiary.ownerPercentage && beneficiary.ownerPercentage !== 0
                  ? getText('contribution.missingInfo')
                  : beneficiary.ownerPercentage + '%'}
              </strong>
            </Text>
          )}
        </div>
      </div>

      <div className={'flex items-center'}>
        {!beneficiary.isCurrentUser && (
          <IconButton
            data-test={'btn-edit-beneficiary'}
            aria-label={getAccessibilityText('button.beneficiary.edit')}
            appearance={'primary'}
            icon={<Edit title={getAccessibilityText('edit')} />}
            onClick={() => setBeneficiaryToEdit(beneficiary)}
            size={'small'}
          />
        )}

        <IconButton
          data-test={'btn-delete-beneficiary'}
          className={'ml-3'}
          appearance={'primary'}
          aria-label={getAccessibilityText('button.beneficiary.delete')}
          icon={<Delete title={getAccessibilityText('delete')} />}
          onClick={() => setBeneficiaryToDelete(beneficiary)}
          size={'small'}
        />
      </div>
    </div>
  );
};

export default BeneficiaryListingItem;
